import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Paper, CircularProgress, Card, Divider, Button, Tooltip } from '@mui/material';
import WaterfallChartIcon from '@mui/icons-material/WaterfallChart';
import Icon from '@mui/material/Icon';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { widget } from '../charting_library';
import CustomReplayDatafeed from './CustomReplayDatafeed';
import { db, doc, getDoc, setDoc } from '../firebaseConfig';
import { useTheme } from '@mui/material/styles';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

// Error Boundary Component
class TradeReplayErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    console.error('ErrorBoundary caught:', error);
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <MDBox p={3}>
          <MDTypography variant="h6" color="error">
            Something went wrong: {this.state.error?.message || 'Unknown error'}
          </MDTypography>
          <Button variant="contained" onClick={() => this.props.navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </MDBox>
      );
    }
    return this.props.children;
  }
}

function getLanguageFromURL() {
  const regex = new RegExp('[\\?&]lang=([^&#]*)');
  const results = regex.exec(window.location.search);
  return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, ' '));
}

const TradeReplay = ({ userID, selectedAccountID = 'default' }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { tradeId } = useParams();
  const chartContainerRef = useRef(null);
  const chartWidgetRef = useRef(null);
  const tradeAnalysisCardRef = useRef(null);
  const tradeDetailsCardRef = useRef(null);
  const [tradeState, setTradeState] = useState(location.state?.trade || null);
  const [analysis, setAnalysis] = useState(null);
  const [score, setScore] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [chartHeight, setChartHeight] = useState('700px');
  const [candles, setCandles] = useState([]);
  const [isChartReady, setIsChartReady] = useState(false);
  const [drawnShapes, setDrawnShapes] = useState([]);
  const datafeedRef = useRef(null);

  const trade = useMemo(() => tradeState, [tradeState]);
  const normalizedSymbol = trade?.symbol ? trade.symbol.replace('/', '') : 'XAUUSD';
  const widgetTheme = theme.palette.mode === 'light' ? 'Light' : 'Dark';

  const parseTradeDate = (trade, isEntry = true) => {
    if (!trade) return null;
    let dateStr;
    if (trade.entryDateTime && isEntry) {
      dateStr = typeof trade.entryDateTime === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trade.entryDateTime)
        ? `${trade.entryDateTime}:00Z`
        : trade.entryDateTime;
    } else if (trade.exitDateTime && !isEntry) {
      dateStr = typeof trade.exitDateTime === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trade.exitDateTime)
        ? `${trade.exitDateTime}:00Z`
        : trade.exitDateTime;
    } else if (trade.date && trade.entryTime && isEntry) {
      dateStr = `${trade.date}T${trade.entryTime}:00Z`;
    } else if (trade.date && trade.exitTime && !isEntry) {
      dateStr = `${trade.date}T${trade.exitTime}:00Z`;
    } else {
      return null;
    }
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const entryDate = parseTradeDate(trade, true);
  const exitDate = parseTradeDate(trade, false);
  const startDateMillis = entryDate ? entryDate.getTime() - 48 * 60 * 60 * 1000 : Date.now() - 7 * 24 * 60 * 60 * 1000;
  const endDateMillis = exitDate ? exitDate.getTime() + 48 * 60 * 60 * 1000 : Date.now();

  // Dynamic timeframe selection
  const tradeDurationHours = entryDate && exitDate
    ? (exitDate.getTime() - entryDate.getTime()) / (1000 * 60 * 60)
    : 1;
  const interval = tradeDurationHours < 0.25 ? '1' : tradeDurationHours < 1 ? '5' : '15';
  const selectedInterval = interval === '1' ? '1min' : interval === '5' ? '5min' : '15min';
  const candleWindow = interval === '1' ? 120 : interval === '5' ? 24 : 8;

  // Dynamic chart height adjustment
  useEffect(() => {
    const updateChartHeight = () => {
      const tradeAnalysisHeight = tradeAnalysisCardRef.current?.getBoundingClientRect().height || 0;
      const tradeDetailsHeight = tradeDetailsCardRef.current?.getBoundingClientRect().height || 0;
      const gap = 32; // Gap between cards (from gap={4}, 4 * 8px = 32px)
      const totalHeight = tradeAnalysisHeight + tradeDetailsHeight + gap;
      setChartHeight(`${totalHeight}px`);
    };

    updateChartHeight();
    window.addEventListener('resize', updateChartHeight);
    return () => window.removeEventListener('resize', updateChartHeight);
  }, [analysis, isLoading]);

  useEffect(() => {
    console.log('TradeReplay mounted. Initial trade from location.state:', location.state);
    console.log('tradeId from useParams:', tradeId);
  }, [tradeId]);

  useEffect(() => {
    console.log('TradeReplay rendered');
  });

  useEffect(() => {
    const fetchTrade = async () => {
      if (!trade && tradeId) {
        console.log('Fetching trade from Firebase:', { userID, selectedAccountID, tradeId });
        try {
          const tradeRef = doc(db, 'Users', userID, 'tradeAccounts', selectedAccountID, 'trades', tradeId);
          const tradeSnap = await getDoc(tradeRef);
          if (tradeSnap.exists()) {
            const tradeData = { id: tradeSnap.id, ...tradeSnap.data() };
            console.log('Fetched trade data:', tradeData);
            setTradeState(tradeData);
          } else {
            console.error('Trade not found:', tradeId);
            setIsLoading(false);
            setAnalysis({ error: 'Trade not found' });
          }
        } catch (error) {
          console.error('Error fetching trade:', error);
          setIsLoading(false);
          setAnalysis({ error: 'Error fetching trade data' });
        }
      } else if (trade) {
        console.log('Using trade from location.state:', trade);
      } else {
        console.error('No trade data or tradeId available');
        setIsLoading(false);
        setAnalysis({ error: 'No trade data available' });
      }
    };

    fetchTrade();
  }, [tradeId, userID, selectedAccountID, trade]);

  const initializeChart = useCallback(() => {
    if (chartContainerRef.current && !chartWidgetRef.current) {
      console.log('Initializing chart widget...');
      datafeedRef.current = new CustomReplayDatafeed({
        symbol: normalizedSymbol,
        interval,
        selectedInterval,
        startDate: new Date(startDateMillis).toISOString(),
        endDate: new Date(endDateMillis).toISOString(),
        userID,
        sessionId: `replay_${trade.id}`,
        startDateMillis,
        endDateMillis,
        trade,
      });

      chartWidgetRef.current = new widget({
        symbol: normalizedSymbol,
        datafeed: datafeedRef.current,
        container: chartContainerRef.current,
        library_path: '/charting_library/',
        locale: getLanguageFromURL() || 'en',
        interval,
        timezone: 'Etc/UTC',
        theme: widgetTheme,
        disabled_features: [
          'use_localstorage_for_settings',
          'timeframes_toolbar',
          'header_interval_dialog_button',
          'show_interval_dialog_on_key_press',
          'header_symbol_search',
          'header_resolutions',
          'time_frames_dropdown',
        ],
        enabled_features: [
          'study_templates',
          'dome_widget',
          'widget_logo',
          'header_widget',
          'indicators',
          'fullscreen_button',
          'drawing',
          'drawing_toolbar',
          'control_bar',
          'chart_crosshair_menu',
          'left_toolbar',
          'disable_resolution_rebuild',
          'side_toolbar_in_fullscreen_mode',
          'header_in_fullscreen_mode',
        ],
        autosize: true,
        debug: true,
        overrides: {
          'mainSeriesProperties.candleStyle.upColor': 'orange',
          'mainSeriesProperties.candleStyle.downColor': 'black',
          'mainSeriesProperties.candleStyle.borderUpColor': 'black',
          'mainSeriesProperties.candleStyle.borderDownColor': 'black',
          'mainSeriesProperties.candleStyle.wickUpColor': 'black',
          'mainSeriesProperties.candleStyle.wickDownColor': 'black',
          'paneProperties.background': widgetTheme === 'Light' ? '#ffffff' : '#131722',
          'scalesProperties.textColor': widgetTheme === 'Light' ? '#000000' : '#ffffff',
          'paneProperties.vertGridProperties.color': widgetTheme === 'Light' ? '#e1e1e1' : '#363c4e',
          'paneProperties.horzGridProperties.color': widgetTheme === 'Light' ? '#e1e1e1' : '#363c4e',
          'toolbar_bg': widgetTheme === 'Light' ? '#f1f3f6' : '#1c2526',
        },
        drawings_access: {
          type: 'white',
          tools: [
            { name: 'Trend Line' },
            { name: 'Long Position' },
            { name: 'Short Position' },
            { name: 'Horizontal Line' },
            { name: 'Vertical Line' },
            { name: 'Arrow' },
            { name: 'Ray' },
            { name: 'Extended Line' },
            { name: 'Parallel Channel' },
            { name: 'Regression Trend' },
            { name: 'Fib Retracement' },
            { name: 'Fib Time Zone' },
            { name: 'Fib Fan' },
            { name: 'Fib Arc' },
            { name: 'Fib Extension' },
            { name: 'Fib Channel' },
            { name: 'Gann Fan' },
            { name: 'Gann Square' },
            { name: 'Gann Box' },
            { name: 'Cycle Lines' },
            { name: 'Text' },
            { name: 'Anchored Text' },
            { name: 'Note' },
            { name: 'Price Label' },
            { name: 'Arrow Marker' },
            { name: 'Balloon' },
            { name: 'Image' },
            { name: 'Measure' },
            { name: 'Date Range' },
            { name: 'Price Range' },
            { name: 'Date and Price Range' },
            { name: 'Projection' },
            { name: 'Cross Line' },
            { name: 'Brush' },
            { name: 'Rectangle' },
            { name: 'Ellipse' },
            { name: 'Triangle' },
            { name: 'Polygon' },
            { name: 'Curve' },
            { name: 'Path' },
            { name: 'Icon' },
            { name: 'Pattern' },
            { name: 'Head and Shoulders' },
            { name: 'Triangle Pattern' },
            { name: 'Double Top/Bottom' },
            { name: 'Triple Top/Bottom' },
            { name: 'Flag' },
            { name: 'Pennant' },
            { name: 'Channel' },
            { name: 'Gartley Pattern' },
            { name: 'Wedge' },
            { name: 'Diamond' },
            { name: 'Rounding Bottom' },
            { name: 'Cup and Handle' },
            { name: 'Complex Head and Shoulders' },
          ],
        },
      });

      chartWidgetRef.current.onChartReady(() => {
        console.log('Chart is ready');
        setIsChartReady(true);
        chartWidgetRef.current.activeChart().setVisibleRange({
          from: startDateMillis / 1000,
          to: endDateMillis / 1000,
        }, () => {
          console.log('Visible range set:', {
            from: new Date(startDateMillis),
            to: new Date(endDateMillis),
          });
        });
      });
    }
  }, [normalizedSymbol, interval, selectedInterval, startDateMillis, endDateMillis, userID, trade, widgetTheme]);

  // Draw trades on the chart using arrows with horizontal lines
  useEffect(() => {
    if (!isChartReady || !chartWidgetRef.current || !trade || !analysis || candles.length === 0) {
      console.log('Cannot draw trades: Missing dependencies', {
        isChartReady,
        chartWidgetRef: !!chartWidgetRef.current,
        trade: !!trade,
        analysis: !!analysis,
        candlesLength: candles.length,
      });
      return;
    }

    console.log('Drawing trades: Dependencies ready', { trade, analysis, candlesLength: candles.length });

    const chart = chartWidgetRef.current.activeChart();
    // Clear previous shapes
    drawnShapes.forEach(shapeId => chart.removeEntity(shapeId));
    const newShapes = [];

    const entryTime = parseTradeDate(trade, true)?.getTime() / 1000;
    const exitTime = parseTradeDate(trade, false)?.getTime() / 1000;
    const entryPrice = parseFloat(trade.entryPrice || trade.entry);
    const exitPrice = parseFloat(trade.exitPrice || trade.exit);
    const slPrice = parseFloat(trade.sl);
    const tpPrice = parseFloat(trade.tp);
    const isLong = trade.shortLong === 'Long';
    const arrowShape = isLong ? 'arrow_up' : 'arrow_down';

    // Determine exitCandle locally
    const exitCandle = candles.find((c) => c.time >= exitTime * 1000) || candles[candles.length - 1];

    // Draw actual trade entry
    if (entryTime && isFinite(entryPrice)) {
      console.log('Drawing actual trade entry...');
      const arrowId = chart.createShape(
        { time: entryTime, price: entryPrice },
        {
          shape: arrowShape,
          text: 'Entry',
          lock: true,
          overrides: {
            color: '#2962FF',
            showLabel: true,
            textcolor: '#2962FF',
            fontsize: 12,
            vertLabelPosition: isLong ? 'top' : 'bottom',
          },
        }
      );
      newShapes.push(arrowId);
      const lineId = chart.createShape(
        { price: entryPrice },
        {
          shape: 'horizontal_line',
          lock: true,
          overrides: {
            linecolor: '#2962FF',
            linestyle: 0,
            linewidth: 1,
          },
        }
      );
      newShapes.push(lineId);
    } else {
      console.log('Cannot draw actual trade entry: Invalid parameters', { entryTime, entryPrice });
    }

    // Draw stop loss at entry time
    if (entryTime && isFinite(slPrice)) {
      console.log('Drawing stop loss...');
      const arrowId = chart.createShape(
        { time: entryTime, price: slPrice },
        {
          shape: arrowShape,
          text: 'SL',
          lock: true,
          overrides: {
            color: '#2962FF',
            showLabel: true,
            textcolor: '#2962FF',
            fontsize: 10,
            vertLabelPosition: isLong ? 'bottom' : 'top',
          },
        }
      );
      newShapes.push(arrowId);
      const lineId = chart.createShape(
        { price: slPrice },
        {
          shape: 'horizontal_line',
          lock: true,
          overrides: {
            linecolor: '#2962FF',
            linestyle: 0,
            linewidth: 1,
          },
        }
      );
      newShapes.push(lineId);
    } else {
      console.log('Cannot draw stop loss: Invalid parameters', { entryTime, slPrice });
    }

    // Draw take profit at entry time
    if (entryTime && isFinite(tpPrice)) {
      console.log('Drawing take profit...');
      const arrowId = chart.createShape(
        { time: entryTime, price: tpPrice },
        {
          shape: arrowShape,
          text: 'TP',
          lock: true,
          overrides: {
            color: '#2962FF',
            showLabel: true,
            textcolor: '#2962FF',
            fontsize: 10,
            vertLabelPosition: isLong ? 'top' : 'bottom',
          },
        }
      );
      newShapes.push(arrowId);
      const lineId = chart.createShape(
        { price: tpPrice },
        {
          shape: 'horizontal_line',
          lock: true,
          overrides: {
            linecolor: '#2962FF',
            linestyle: 0,
            linewidth: 1,
          },
        }
      );
      newShapes.push(lineId);
    } else {
      console.log('Cannot draw take profit: Invalid parameters', { entryTime, tpPrice });
    }

    // Draw actual trade exit
    if (exitTime && isFinite(exitPrice)) {
      console.log('Drawing actual trade exit...');
      const arrowId = chart.createShape(
        { time: exitTime, price: exitPrice },
        {
          shape: arrowShape,
          text: 'Exit',
          lock: true,
          overrides: {
            color: '#2962FF',
            showLabel: true,
            textcolor: '#2962FF',
            fontsize: 12,
            vertLabelPosition: isLong ? 'top' : 'bottom',
          },
        }
      );
      newShapes.push(arrowId);
      const lineId = chart.createShape(
        { price: exitPrice },
        {
          shape: 'horizontal_line',
          lock: true,
          overrides: {
            linecolor: '#2962FF',
            linestyle: 0,
            linewidth: 1,
          },
        }
      );
      newShapes.push(lineId);
    } else {
      console.log('Cannot draw actual trade exit: Invalid parameters', { exitTime, exitPrice });
    }

    // Draw optimal trade markers
    if (analysis.optimalEntry && analysis.optimalSL && analysis.optimalTP) {
      const optimalEntryPrice = parseFloat(analysis.optimalEntry);
      const optimalTP = parseFloat(analysis.optimalTP);
      const optimalSL = parseFloat(analysis.optimalSL);

      let optimalEntryTime = entryTime;
      let minPriceDiff = Infinity;
      candles.forEach((candle) => {
        const priceDiff = Math.abs(candle.low - optimalEntryPrice);
        if (priceDiff < minPriceDiff) {
          minPriceDiff = priceDiff;
          optimalEntryTime = candle.time / 1000;
        }
      });

      const sortedCandles = [...candles].sort((a, b) => a.time - b.time);
      const duringTradeCandles = sortedCandles.filter(
        (c) => c.time / 1000 >= entryTime && c.time / 1000 <= exitDate.getTime() / 1000
      );
      let bestExitPriceDuringTrade = exitPrice;
      let bestExitCandleDuringTrade = candles.find((c) => c.time >= exitTime * 1000) || candles[candles.length - 1];
      const exitIndex = sortedCandles.findIndex((c) => c.time === bestExitCandleDuringTrade.time);
      const maxPostExitCandles = interval === '1' ? 30 : interval === '5' ? 36 : interval === '15' ? 24 : 24;
      const relevantPostExitCandles = sortedCandles.slice(exitIndex + 1, exitIndex + 1 + maxPostExitCandles);
      const optimalExitCandles = [...duringTradeCandles, ...relevantPostExitCandles];
      bestExitCandleDuringTrade = optimalExitCandles.reduce((prev, current) => 
        (isLong && current.high > prev.high) || (!isLong && current.low < prev.low) ? current : prev,
        optimalExitCandles[0]
      );
      const optimalExitTime = bestExitCandleDuringTrade ? bestExitCandleDuringTrade.time / 1000 : exitTime;

      if (optimalEntryTime && isFinite(optimalEntryPrice)) {
        console.log('Drawing optimal trade entry...');
        const arrowId = chart.createShape(
          { time: optimalEntryTime, price: optimalEntryPrice },
          {
            shape: arrowShape,
            text: 'Opt. Entry',
            lock: true,
            overrides: {
              color: '#00C853',
              showLabel: true,
              textcolor: '#00C853',
              fontsize: 12,
              vertLabelPosition: isLong ? 'top' : 'bottom',
            },
          }
        );
        newShapes.push(arrowId);
        const lineId = chart.createShape(
          { price: optimalEntryPrice },
          {
            shape: 'horizontal_line',
            lock: true,
            overrides: {
              linecolor: '#00C853',
              linestyle: 0,
              linewidth: 1,
            },
          }
        );
        newShapes.push(lineId);
      } else {
        console.log('Cannot draw optimal trade entry: Invalid parameters', { optimalEntryTime, optimalEntryPrice });
      }

      if (optimalEntryTime && isFinite(optimalSL)) {
        console.log('Drawing optimal stop loss...');
        const arrowId = chart.createShape(
          { time: optimalEntryTime, price: optimalSL },
          {
            shape: arrowShape,
            text: 'Opt. SL',
            lock: true,
            overrides: {
              color: '#00C853',
              showLabel: true,
              textcolor: '#00C853',
              fontsize: 10,
              vertLabelPosition: isLong ? 'bottom' : 'top',
            },
          }
        );
        newShapes.push(arrowId);
        const lineId = chart.createShape(
          { price: optimalSL },
          {
            shape: 'horizontal_line',
            lock: true,
            overrides: {
              linecolor: '#00C853',
              linestyle: 0,
              linewidth: 1,
            },
          }
        );
        newShapes.push(lineId);
      } else {
        console.log('Cannot draw optimal stop loss: Invalid parameters', { optimalEntryTime, optimalSL });
      }

      if (optimalExitTime && isFinite(optimalTP)) {
        console.log('Drawing optimal take profit...');
        const arrowId = chart.createShape(
          { time: optimalExitTime, price: optimalTP },
          {
            shape: arrowShape,
            text: 'Opt. TP/Exit',
            lock: true,
            overrides: {
              color: '#00C853',
              showLabel: true,
              textcolor: '#00C853',
              fontsize: 10,
              vertLabelPosition: isLong ? 'top' : 'bottom',
            },
          }
        );
        newShapes.push(arrowId);
        const lineId = chart.createShape(
          { price: optimalTP },
          {
            shape: 'horizontal_line',
            lock: true,
            overrides: {
              linecolor: '#00C853',
              linestyle: 0,
              linewidth: 1,
            },
          }
        );
        newShapes.push(lineId);
      } else {
        console.log('Cannot draw optimal take profit: Invalid parameters', { optimalExitTime, optimalTP });
      }
    }

    setDrawnShapes(newShapes);
  }, [isChartReady, trade, analysis, candles, parseTradeDate, chartWidgetRef, drawnShapes, setDrawnShapes]);

  useEffect(() => {
    if (!trade || !trade.id || !trade.symbol) {
      console.log('Skipping initializeReplay: Missing trade ID or symbol');
      setIsLoading(false);
      setAnalysis({ error: 'Invalid trade data: Missing ID or symbol' });
      return;
    }

    const timer = setTimeout(() => {
      initializeChart();
      initializeReplay();
    }, 100);

    async function initializeReplay() {
      setIsLoading(true);
      const currentTrade = trade;
      console.log('Starting initializeReplay for trade:', currentTrade);
      try {
        const entryTime = entryDate ? entryDate.getTime() : startDateMillis;
        const exitTime = exitDate ? exitDate.getTime() : endDateMillis - 48 * 60 * 60 * 1000;
        console.log('Date range:', { startDate: new Date(startDateMillis), endDate: new Date(endDateMillis) });
        const sessionId = `replay_${currentTrade.id}`;

        const replayRef = doc(db, 'Users', userID, 'tradeAccounts', selectedAccountID, 'trades', currentTrade.id, 'tradeReplays', `replay_${currentTrade.id}`);
        let candlesData = [];
        let analysisResult = null;
        let scoreResult = 0;

        const replaySnap = await getDoc(replayRef);
        if (replaySnap.exists()) {
          console.log('Using cached replay data from Firebase');
          const replayData = replaySnap.data();
          candlesData = replayData.candles
            .map((c) => ({
              ...c,
              time: c.time < 10000000000 ? c.time * 1000 : c.time,
            }))
            .filter((c) => c.time >= startDateMillis && c.time <= endDateMillis);
          console.log('Cached candles filtered (48-hour limit):', candlesData.length);
          analysisResult = replayData.analysis;
          scoreResult = replayData.score;
        } else {
          datafeedRef.current = new CustomReplayDatafeed({
            symbol: normalizedSymbol,
            interval,
            selectedInterval,
            startDate: new Date(startDateMillis).toISOString(),
            endDate: new Date(endDateMillis).toISOString(),
            userID,
            sessionId,
            startDateMillis,
            endDateMillis,
            trade: currentTrade,
          });

          console.log('Fetching candles for:', normalizedSymbol);
          candlesData = await datafeedRef.current.fetchAllBarsFromFirestore(interval);
          console.log('Raw candles fetched:', candlesData.length, 'First:', candlesData[0], 'Last:', candlesData[candlesData.length - 1]);
          candlesData = candlesData
            .map((c) => ({
              ...c,
              time: c.time < 10000000000 ? c.time * 1000 : c.time,
            }))
            .filter((c) => c.time >= startDateMillis && c.time <= endDateMillis);
          console.log('Filtered candles (48-hour limit):', candlesData.length);
          if (candlesData.length === 0) {
            console.error('No candle data available after filtering');
            setIsLoading(false);
            setAnalysis({ error: 'No candle data available for the selected timeframe' });
            return;
          }

          analysisResult = analyzeTrade(currentTrade, candlesData, candleWindow);
          console.log('Analysis result:', analysisResult);
          scoreResult = calculateScore(analysisResult);

          await setDoc(replayRef, {
            candles: candlesData,
            analysis: analysisResult,
            score: scoreResult,
            timestamp: new Date().toISOString(),
          });
          console.log('Saved replay data to Firebase');
        }

        setCandles(candlesData);
        setAnalysis(analysisResult);
        setScore(scoreResult);
        setIsLoading(false);
      } catch (error) {
        console.error('Error in initializeReplay:', error);
        setIsLoading(false);
        setAnalysis({ error: `Failed to initialize replay: ${error.message}` });
      }
    }

    return () => {
      console.log('Cleaning up chart widget...');
      clearTimeout(timer);
      if (chartContainerRef.current) {
        chartContainerRef.current.innerHTML = '';
      }
      if (chartWidgetRef.current) {
        chartWidgetRef.current.remove();
        chartWidgetRef.current = null;
      }
      setIsChartReady(false);
      setDrawnShapes([]);
    };
  }, [trade, userID, normalizedSymbol, selectedAccountID, widgetTheme, startDateMillis, endDateMillis, interval, selectedInterval, candleWindow, initializeChart]);

  const analyzeTrade = (trade, candles, candleWindow = 8) => {
    if (!trade || !candles || !Array.isArray(candles) || candles.length === 0) {
      console.error('Invalid trade or candles:', { trade, candles });
      return { error: 'Invalid trade or no candle data available' };
    }

    if (!trade.shortLong || !['Long', 'Short'].includes(trade.shortLong)) {
      return { error: 'Invalid trade direction (shortLong must be "Long" or "Short")' };
    }

    const entryPrice = parseFloat(trade.entryPrice || trade.entry);
    if (!isFinite(entryPrice)) {
      console.error('No valid entryPrice in trade:', trade);
      return { error: 'No valid entry price available' };
    }

    const exitPrice = parseFloat(trade.exitPrice || trade.exit);
    if (!isFinite(exitPrice)) {
      console.error('No valid exitPrice in trade:', trade);
      return { error: 'No valid exit price available' };
    }

    const slPrice = parseFloat(trade.sl);
    if (!isFinite(slPrice)) {
      console.error('No valid stop loss in trade:', trade);
      return { error: 'No valid stop loss available' };
    }

    const tpPrice = parseFloat(trade.tp);
    console.log('Analyzing trade:', {
      symbol: trade.symbol,
      shortLong: trade.shortLong,
      entryPrice,
      exitPrice,
      slPrice,
      tpPrice,
      entryTime: trade.entryDateTime,
      exitTime: trade.exitDateTime,
    });

    const getPipSize = (symbol) => {
      if (!symbol) return 0.0001;
      if (symbol === 'XAUUSD') return 0.01;
      const jpyPairs = ['USDJPY', 'GBPJPY', 'AUDJPY', 'NZDJPY', 'CADJPY', 'CHFJPY'];
      return jpyPairs.includes(symbol) ? 0.01 : 0.0001;
    };
    const getPipThreshold = (symbol) => (symbol === 'XAUUSD' ? 1000 : 5);

    const pipSize = getPipSize(trade.symbol);
    const pipThreshold = getPipThreshold(trade.symbol);
    console.log(`Using pip size ${pipSize}, threshold ${pipThreshold} for symbol ${trade.symbol}`);

    const entryTime = parseTradeDate(trade, true)?.getTime() || Date.now() - 7 * 24 * 60 * 60 * 1000;
    const exitTime = parseTradeDate(trade, false)?.getTime() || Date.now();
    const maxAnalysisTime = exitTime + 48 * 60 * 60 * 1000;

    const sortedCandles = [...candles].sort((a, b) => a.time - b.time);
    console.log('Candles received:', sortedCandles.length, 'First:', sortedCandles[0], 'Last:', sortedCandles[sortedCandles.length - 1]);
    sortedCandles.forEach((c, i) => {
      if (i < 2 || i >= sortedCandles.length - 2) {
        console.log(`Candle ${i}:`, {
          time: new Date(c.time).toISOString(),
          open: c.open,
          high: c.high,
          low: c.low,
          close: c.close,
        });
      }
    });

    const entryCandle = sortedCandles.find((c) => c.time >= entryTime) || sortedCandles[0];
    const exitCandle = sortedCandles.find((c) => c.time >= exitTime) || sortedCandles[sortedCandles.length - 1];
    const entryIndex = sortedCandles.indexOf(entryCandle);
    const exitIndex = sortedCandles.indexOf(exitCandle);

    const entryWindowTimeMs = interval === '1' ? 30 * 60 * 1000 // 30 minutes for 1min
      : interval === '5' ? 3 * 60 * 60 * 1000 // 3 hours for 5min
      : interval === '15' ? 6 * 60 * 60 * 1000 // 6 hours for 15min
      : 24 * 60 * 60 * 1000; // 24 hours for 1hr
    const entryWindowCandles = sortedCandles
      .slice(Math.max(0, entryIndex - Math.floor(entryWindowTimeMs / (interval * 60 * 1000))), entryIndex + Math.floor(entryWindowTimeMs / (interval * 60 * 1000)) + 1)
      .filter((c) => c.time <= maxAnalysisTime);

    const postEntryLow = entryWindowCandles.length > 0
      ? Math.min(...entryWindowCandles.map((c) => c.low))
      : entryPrice;
    const postEntryHigh = entryWindowCandles.length > 0
      ? Math.max(...entryWindowCandles.map((c) => c.high))
      : entryPrice;
    const entryPriceRange = postEntryHigh - postEntryLow;
    const entryPriceRangePips = entryPriceRange / pipSize;
    const bestEntryPrice = trade.shortLong === 'Long' ? postEntryLow : postEntryHigh;
    const entryPipMiss = Math.abs(entryPrice - bestEntryPrice) / pipSize;

    const duringTradeCandles = sortedCandles.slice(entryIndex, exitIndex + 1);
    const tradeDirectionCorrect = trade.shortLong === 'Long'
      ? duringTradeCandles.some((c) => c.high >= entryPrice + pipThreshold * pipSize)
      : duringTradeCandles.some((c) => c.low <= entryPrice - pipThreshold * pipSize);

    let entryPercentOfBest;
    if (entryPriceRangePips <= 0) {
      entryPercentOfBest = 100.00;
    } else if (trade.shortLong === 'Long') {
      entryPercentOfBest = ((postEntryHigh - entryPrice) / (postEntryHigh - postEntryLow) * 100).toFixed(2);
    } else {
      entryPercentOfBest = ((entryPrice - postEntryLow) / (postEntryHigh - postEntryLow) * 100).toFixed(2);
    }

    let entryScore = 0;
    let entryText = '';
    if (tradeDirectionCorrect) {
      entryScore = 1;
      if (entryPercentOfBest >= 75) {
        entryScore = 2;
        entryText = `Entered at $${entryPrice.toFixed(5)}, close to optimal entry at $${bestEntryPrice.toFixed(5)} (${entryPipMiss.toFixed(1)} pips from best). Captured ${entryPercentOfBest}% of optimal entry range. Optimal timing.`;
      } else {
        entryText = `Entered at $${entryPrice.toFixed(5)}, but optimal entry was at $${bestEntryPrice.toFixed(5)} (${entryPipMiss.toFixed(1)} pips from best). Captured ${entryPercentOfBest}% of optimal entry range. Directionally correct, but consider waiting for a better entry.`;
      }
    } else {
      entryText = `Entered at $${entryPrice.toFixed(5)}, but price did not move in the favorable direction significantly (${entryPipMiss.toFixed(1)} pips from best: $${bestEntryPrice.toFixed(5)}). Captured ${entryPercentOfBest}% of optimal entry range. Timing could be improved.`;
    }

    // Peak detection with limited post-exit candles
    let firstPeakTime = entryTime;
    let firstPeakPrice = entryPrice;
    let maxExitTime = exitTime;
    let bestExitPriceDuringTrade = exitPrice;
    let bestExitCandleDuringTrade = exitCandle;
    const significantPipThreshold = pipThreshold;
    let peakCandles = [];
    const maxPostExitCandles = interval === '1' ? 30 : interval === '5' ? 36 : interval === '15' ? 24 : 24;
    const relevantPostExitCandles = sortedCandles.slice(exitIndex + 1, exitIndex + 1 + maxPostExitCandles);
    const allRelevantCandles = [...duringTradeCandles, ...relevantPostExitCandles];

    allRelevantCandles.forEach((candle, i) => {
      const isPeak = trade.shortLong === 'Long'
        ? (i === 0 || candle.high > Math.max(...allRelevantCandles.slice(0, i).map(c => c.high))) &&
          (i === allRelevantCandles.length - 1 || candle.high > Math.max(...allRelevantCandles.slice(i + 1).map(c => c.high)))
        : (i === 0 || candle.low < Math.min(...allRelevantCandles.slice(0, i).map(c => c.low))) &&
          (i === allRelevantCandles.length - 1 || candle.low < Math.min(...allRelevantCandles.slice(i + 1).map(c => c.low)));
      if (isPeak) {
        const peakPrice = trade.shortLong === 'Long' ? candle.high : candle.low;
        if (Math.abs(peakPrice - entryPrice) / pipSize >= significantPipThreshold) {
          peakCandles.push({ time: candle.time, price: peakPrice });
          if (firstPeakTime === entryTime) {
            firstPeakTime = candle.time;
            firstPeakPrice = peakPrice;
          }
        }
      }
      if (trade.shortLong === 'Long' && candle.high > bestExitPriceDuringTrade) {
        bestExitPriceDuringTrade = candle.high;
        bestExitCandleDuringTrade = candle;
        maxExitTime = candle.time;
      } else if (trade.shortLong === 'Short' && candle.low < bestExitPriceDuringTrade) {
        bestExitPriceDuringTrade = candle.low;
        bestExitCandleDuringTrade = candle;
        maxExitTime = candle.time;
      }
    });
    const optimalHoldTime = Math.max((maxExitTime - entryTime) / (1000 * 60 * 60), 0);
    const firstPeakHoldTime = firstPeakTime > entryTime ? (firstPeakTime - entryTime) / (1000 * 60 * 60) : (exitTime - entryTime) / (1000 * 60 * 60);
    const bestExitPipGain = Math.abs(bestExitPriceDuringTrade - entryPrice) / pipSize;

    const timeToleranceMinutes = 15;
    const timeToleranceMs = timeToleranceMinutes * 60 * 1000;
    const isNearFirstPeak = firstPeakTime > entryTime && Math.abs(exitTime - firstPeakTime) <= timeToleranceMs;
    const isNearOptimalExit = Math.abs(exitTime - maxExitTime) <= timeToleranceMs;
    const missedFirstPeak = firstPeakTime > entryTime && !isNearFirstPeak;
    const missedOptimalExit = maxExitTime > exitTime && !isNearOptimalExit;

    const postExitHigh = relevantPostExitCandles.length > 0
      ? Math.max(...relevantPostExitCandles.map((c) => c.high))
      : exitPrice;
    const postExitLow = relevantPostExitCandles.length > 0
      ? Math.min(...relevantPostExitCandles.map((c) => c.low))
      : exitPrice;
    const bestExitPrice = trade.shortLong === 'Long'
      ? Math.max(bestExitPriceDuringTrade, postExitHigh)
      : Math.min(bestExitPriceDuringTrade, postExitLow);
    const optimalTP = bestExitPrice;
    const exitPriceRange = trade.shortLong === 'Long'
      ? optimalTP - entryPrice
      : entryPrice - optimalTP;
    const exitPriceRangePips = exitPriceRange / pipSize;
    const actualReward = trade.shortLong === 'Long' ? exitPrice - entryPrice : entryPrice - exitPrice;
    const maxAchievableReward = trade.shortLong === 'Long' ? optimalTP - entryPrice : entryPrice - optimalTP;
    const exitPercentOfBest = maxAchievableReward > 0
      ? Math.min(100, (Math.abs(actualReward) / Math.abs(maxAchievableReward) * 100)).toFixed(2)
      : 100.00;
    const exitPipMiss = Math.abs(optimalTP - exitPrice) / pipSize;
    const isSameCandleExit = entryCandle.time === exitCandle.time;
    const missedOpportunity = exitPipMiss > pipThreshold && !isSameCandleExit;
    const bestExitTime = maxExitTime / 1000;
    const exitedEarly = bestExitTime > exitTime && missedOpportunity;
    const tpTooLow = isFinite(tpPrice) && (trade.shortLong === 'Long' ? tpPrice < optimalTP : tpPrice > optimalTP);
    let exitScore = isSameCandleExit || !missedOpportunity ? 2 : 1;
    if (tpTooLow) {
      exitScore = exitPercentOfBest >= 75 && Math.abs(exitTime - maxExitTime) <= timeToleranceMs ? 2
        : exitPercentOfBest >= 50 ? 1
        : 0;
    }
    const formattedOptimalExitTime = new Date(maxExitTime).toISOString().slice(0, 10).split('-').reverse().join('-') + ' ' + new Date(maxExitTime).toISOString().slice(11, 19);
    const exitText = isSameCandleExit
      ? `Exited at $${exitPrice.toFixed(5)} within the same candle as entry (${exitPipMiss.toFixed(1)} pips from optimal TP/exit at $${optimalTP.toFixed(5)}). Captured ${exitPercentOfBest}% of optimal exit. Consider monitoring intraday price action for better exit timing.`
      : exitedEarly
        ? `Exited at $${exitPrice.toFixed(5)}, but price reached optimal TP/exit at $${optimalTP.toFixed(5)} (${exitPipMiss.toFixed(1)} pips from best) at ${formattedOptimalExitTime}. Captured ${exitPercentOfBest}% of optimal exit. Consider exiting earlier or taking partial profits at key levels.`
        : missedOpportunity && tpTooLow
          ? `Exited at $${exitPrice.toFixed(5)} (TP set at $${tpPrice.toFixed(5)} was too low), but price reached optimal TP/exit at $${optimalTP.toFixed(5)} (${exitPipMiss.toFixed(1)} pips from best) at ${formattedOptimalExitTime}. Captured ${exitPercentOfBest}% of optimal exit. Adjust TP to capture more profit, though exit was near a key peak.`
          : `Exited at $${exitPrice.toFixed(5)}, ${exitPipMiss.toFixed(1)} pips from optimal TP/exit at $${optimalTP.toFixed(5)}. Captured ${exitPercentOfBest}% of optimal exit. Optimal timing.`;

    const lowestDuringTrade = trade.shortLong === 'Long'
      ? Math.min(...duringTradeCandles.map((c) => c.low))
      : Math.max(...duringTradeCandles.map((c) => c.high));
    const atr = calculateATR(sortedCandles.slice(Math.max(0, entryIndex - 14), entryIndex), 14);
    const atrAdjusted = atr * 1.5;
    const optimalSL = trade.shortLong === 'Long'
      ? Math.max(slPrice, lowestDuringTrade - atrAdjusted)
      : Math.min(slPrice, lowestDuringTrade + atrAdjusted);
    const actualRisk = Math.abs(entryPrice - slPrice);
    const suggestedRisk = Math.abs(entryPrice - optimalSL);
    const actualRewardRR = trade.shortLong === 'Long' ? exitPrice - entryPrice : entryPrice - exitPrice;
    const maxAchievableRewardRR = trade.shortLong === 'Long' ? optimalTP - entryPrice : entryPrice - optimalTP;
    const actualRR = actualRisk > 0 ? Math.abs(actualRewardRR / actualRisk) : 0;
    const maxAchievableRR = actualRisk > 0 ? Math.abs(maxAchievableRewardRR / actualRisk) : 0;
    const rrPercentOfBest = maxAchievableRR > 0 ? (actualRR / maxAchievableRR * 100).toFixed(2) : 100.00;
    let rrScore = actualRR >= maxAchievableRR * 0.9
      ? 2
      : actualRR >= maxAchievableRR * 0.7
      ? 1
      : 0;
    if (tpTooLow && exitPipMiss > 5) rrScore = 0;
    const rrText = tpTooLow
      ? `Risk:Reward: 1:${actualRR.toFixed(1)} vs Maximum Achievable: 1:${maxAchievableRR.toFixed(1)} (${rrPercentOfBest}% of max). TP at $${tpPrice.toFixed(5)} was too low, limiting potential reward. Suggested TP at $${optimalTP.toFixed(5)}.`
      : `Risk:Reward: 1:${actualRR.toFixed(1)} vs Maximum Achievable: 1:${maxAchievableRR.toFixed(1)} (${rrPercentOfBest}% of max). Your SL at $${slPrice.toFixed(5)} was optimal based on price action and volatility.`;

    const isBreakevenSet = Math.abs(slPrice - entryPrice) < pipSize;
    const tradeWentPositive = trade.shortLong === 'Long'
      ? duringTradeCandles.some((c) => c.high >= entryPrice + pipThreshold * pipSize)
      : duringTradeCandles.some((c) => c.low <= entryPrice - pipThreshold * pipSize);
    const priceReturnedToEntry = relevantPostExitCandles.some((c) =>
      trade.shortLong === 'Long'
        ? c.low <= entryPrice + pipSize
        : c.high >= entryPrice - pipSize
    );
    const tpHit = isFinite(tpPrice) && (
      trade.shortLong === 'Long'
        ? duringTradeCandles.some((c) => c.high >= tpPrice)
        : duringTradeCandles.some((c) => c.low <= tpPrice)
    );
    let tpHitBeforeReturn = false;
    if (tpHit && priceReturnedToEntry) {
      const tpCandle = duringTradeCandles.find((c) =>
        trade.shortLong === 'Long' ? c.high >= tpPrice : c.low <= tpPrice
      );
      const returnCandle = relevantPostExitCandles.find((c) =>
        trade.shortLong === 'Long' ? c.low <= entryPrice + pipSize : c.high >= entryPrice - pipSize
      );
      tpHitBeforeReturn = tpCandle && returnCandle && tpCandle.time <= returnCandle.time;
    }
    const missedProfitAfterBE = tradeWentPositive && priceReturnedToEntry && relevantPostExitCandles.some((c) =>
      trade.shortLong === 'Long'
        ? c.high > entryPrice + pipThreshold * pipSize
        : c.low < entryPrice - pipThreshold * pipSize
    ) && !tpHitBeforeReturn;

    let beScore, beText;
    if (isBreakevenSet) {
      if (exitPrice === entryPrice) {
        if (missedProfitAfterBE) {
          beScore = 1;
          beText = `Breakeven set at $${entryPrice.toFixed(5)} and closed at breakeven, but price moved favorably to ${
            trade.shortLong === 'Long' ? `$${postExitHigh.toFixed(5)}` : `$${postExitLow.toFixed(5)}`
          } afterward within 3 hours. Consider using a trailing stop to capture more profit.`;
        } else {
          beScore = 2;
          beText = `Breakeven set and closed at $${entryPrice.toFixed(5)} with no significant favorable movement post-exit within 3 hours. Appropriate use of breakeven.`;
        }
      } else {
        beScore = tradeWentPositive ? 1 : 2;
        beText = `Breakeven set at $${entryPrice.toFixed(5)}, but exited at $${exitPrice.toFixed(5)}. ${
          tradeWentPositive ? 'Consider trailing stops to protect profits.' : 'Breakeven was set appropriately but not hit.'
        }`;
      }
    } else {
      if (tpHit && !priceReturnedToEntry) {
        beScore = 2;
        beText = `No breakeven applied; take profit hit at $${tpPrice.toFixed(5)} before price returned to entry within 3 hours. Breakeven was not necessary.`;
      } else if (tpHit && priceReturnedToEntry && tpHitBeforeReturn) {
        beScore = 2;
        beText = `No breakeven applied; take profit hit at $${tpPrice.toFixed(5)} before price returned to entry at $${entryPrice.toFixed(5)} within 3 hours. Breakeven was not necessary as TP was achieved.`;
      } else if (tradeWentPositive && priceReturnedToEntry) {
        beScore = 1;
        beText = `No breakeven applied; exited at $${exitPrice.toFixed(5)}. Price moved favorably but returned to entry at $${entryPrice.toFixed(5)} within 3 hours after TP. Consider setting breakeven or trailing stops after reaching TP to protect profits.`;
      } else {
        beScore = 2;
        beText = `No breakeven applied; exited at $${exitPrice.toFixed(5)}. ${
          tradeWentPositive ? 'Price did not return to entry within 3 hours, so breakeven was not necessary.' : 'No significant favorable movement to warrant breakeven.'
        }`;
      }
    }

    const volatilityScore =
      atr === 0 ? 0 : Math.abs(entryPrice - slPrice) >= atr * 0.5 ? 1 : 0;
    const volatilityText = atr === 0
      ? `Unable to calculate ATR (insufficient pre-entry candles: ${sortedCandles.slice(Math.max(0, entryIndex - 14), entryIndex).length}). SL at $${slPrice.toFixed(5)}.`
      : volatilityScore
      ? `SL at $${slPrice.toFixed(5)} aligns with volatility (ATR: ${atr.toFixed(5)}).`
      : `SL at $${slPrice.toFixed(5)} is too tight for volatility (ATR: ${atr.toFixed(5)}). Consider wider SL.`;

    const holdTime = Math.min((exitTime - entryTime) / (1000 * 60 * 60), 48);

    let durationScore = 1;
    if (missedFirstPeak && missedOptimalExit) {
      durationScore = 0;
    } else if (!isNearFirstPeak && !isNearOptimalExit) {
      durationScore = 0; // Strict penalty if neither is near
    }

    const atrForTrailing = calculateATR(sortedCandles.slice(Math.max(0, entryIndex - 14), entryIndex), 14);
    const trailingStopPips = Math.max(atrForTrailing / pipSize * 1.5, 15); // Dynamic trailing stop
    let durationText = '';
    if (isNearFirstPeak) {
      durationText = `Held for ${holdTime.toFixed(1)} hours, near the first peak at ${firstPeakHoldTime.toFixed(1)} hours (${new Date(firstPeakTime).toLocaleString('en-GB', { timeZone: 'UTC' }).slice(0, -3)} to capture $${firstPeakPrice.toFixed(5)}). Well-timed exit at the first opportunity.`;
    } else if (isNearOptimalExit) {
      durationText = `Held for ${holdTime.toFixed(1)} hours, near the optimal ${optimalHoldTime.toFixed(1)} hours. Exited close to ${new Date(maxExitTime).toLocaleString('en-GB', { timeZone: 'UTC' }).slice(0, -3)} to capture $${optimalTP.toFixed(5)}. Well-timed exit.`;
    } else if (missedFirstPeak && missedOptimalExit) {
      const pipGainFirstPeak = Math.abs(firstPeakPrice - entryPrice) / pipSize;
      durationText = `Held for ${holdTime.toFixed(1)} hours, missing the first peak at ${firstPeakHoldTime.toFixed(1)} hours (${new Date(firstPeakTime).toLocaleString('en-GB', { timeZone: 'UTC' }).slice(0, -3)} to capture $${firstPeakPrice.toFixed(5)}, ${pipGainFirstPeak.toFixed(1)} pips) and the optimal exit at ${optimalHoldTime.toFixed(1)} hours (${new Date(maxExitTime).toLocaleString('en-GB', { timeZone: 'UTC' }).slice(0, -3)} to capture $${optimalTP.toFixed(5)}, ${exitPipMiss.toFixed(1)} pips missed). Consider exiting at the first peak or holding to the optimal TP with a trailing stop of ${trailingStopPips.toFixed(1)} pips.`;
    } else if (missedFirstPeak) {
      const pipGainFirstPeak = Math.abs(firstPeakPrice - entryPrice) / pipSize;
      durationText = `Held for ${holdTime.toFixed(1)} hours, missing the first peak at ${firstPeakHoldTime.toFixed(1)} hours (${new Date(firstPeakTime).toLocaleString('en-GB', { timeZone: 'UTC' }).slice(0, -3)} to capture $${firstPeakPrice.toFixed(5)}, ${pipGainFirstPeak.toFixed(1)} pips). Consider exiting earlier at the first peak or using a trailing stop.`;
    } else if (missedOptimalExit) {
      durationText = `Held for ${holdTime.toFixed(1)} hours, shorter than the optimal ${optimalHoldTime.toFixed(1)} hours. Could have waited until ${new Date(maxExitTime).toLocaleString('en-GB', { timeZone: 'UTC' }).slice(0, -3)} to capture $${optimalTP.toFixed(5)} (${exitPipMiss.toFixed(1)} pips missed). Consider holding longer or using a trailing stop.`;
    }

    return {
      entry: { score: entryScore, text: entryText },
      exit: { score: exitScore, text: exitText },
      rr: { score: rrScore, text: rrText },
      be: { score: beScore, text: beText },
      volatility: { score: volatilityScore, text: volatilityText },
      duration: { score: durationScore, text: durationText },
      optimalEntry: isFinite(bestEntryPrice) ? bestEntryPrice : 'N/A',
      optimalSL: isFinite(optimalSL) ? optimalSL : 'N/A',
      optimalTP: isFinite(optimalTP) ? optimalTP : 'N/A',
      note: `Analysis based on a 48-hour window post-trade exit using ${selectedInterval} candles. Candle data is sourced from a third-party provider and may not align perfectly with your broker's data. Our AI can make mistakes too, treat this as an estimation only.`,
    };
  };
  const calculateATR = (candles, period) => {
    if (!candles || candles.length < period) {
      console.warn('Insufficient candles for ATR:', candles?.length || 0);
      return 0;
    }
    const trs = candles.slice(-period).map((c, i) => {
      if (i === 0) return c.high - c.low;
      const prevClose = candles[i - 1].close;
      return Math.max(
        c.high - c.low,
        Math.abs(c.high - prevClose),
        Math.abs(c.low - prevClose)
      );
    });
    return trs.reduce((sum, tr) => sum + tr, 0) / period;
  };

  const calculateScore = (analysis) => {
    if (!analysis || analysis.error) return 0;
    return (
      analysis.entry.score +
      analysis.exit.score +
      analysis.rr.score +
      analysis.be.score +
      analysis.volatility.score +
      analysis.duration.score
    );
  };

  const formatDateUTC = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleString('en-GB', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).replace(',', '').slice(0, -3);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar
        tradeAccounts={location.state?.tradeAccounts || []}
        setSelectedAccountID={location.state?.setSelectedAccountID || (() => {})}
      />
      <TradeReplayErrorBoundary navigate={navigate}>
        <MDBox py={3}>
          <MDTypography variant="h4" gutterBottom>
            Trade Replay: {trade?.symbol || 'Loading...'} (
            {formatDateUTC(entryDate)})
          </MDTypography>
          <MDBox display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={4}>
            <MDBox flex={3}>
              <Paper sx={{ height: chartHeight, borderRadius: '8px', overflow: 'visible' }}>
                <div ref={chartContainerRef} style={{ height: '100%' }} id={`tradingview-chart-${tradeId}`} />
                {isLoading && (
                  <MDBox sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress />
                  </MDBox>
                )}
              </Paper>
            </MDBox>
            <MDBox flex={1} display="flex" flexDirection="column" gap={4}>
              {isLoading ? (
                <MDBox flex={1} display="flex" justifyContent="center" alignItems="center">
                  <CircularProgress />
                </MDBox>
              ) : (
                <>
                  <Card
                    ref={tradeAnalysisCardRef}
                    sx={{
                      minHeight: { xs: '50vh', md: '40vh' },
                      overflow: 'visible',
                    }}
                  >
                    <MDBox
                      variant="gradient"
                      bgColor="light"
                      color="dark"
                      coloredShadow="light"
                      borderRadius="xl"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="4rem"
                      height="4rem"
                      mt={-3}
                      mx={2}
                      zIndex={2}
                    >
                      <WaterfallChartIcon fontSize="medium" color="inherit" />
                    </MDBox>
                    <MDBox display="flex" flexDirection="column" pt={1} px={2} sx={{ height: '100%', overflow: 'auto' }}>
                      <MDBox display="flex" justifyContent="flex-end">
                        <MDBox textAlign="right" lineHeight={1.25}>
                          <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
                          Trade Analysis
        </MDTypography>
        <MDBox display="flex" justifyContent="flex-end" alignItems="center">
          <MDTypography variant="h5" fontFamily="inherit">
            {analysis?.error ? 'N/A' : `${score}/10`}
          </MDTypography>
          <Tooltip
            title={
<MDTypography variant="caption" color="text" fontFamily="inherit" sx={{ whiteSpace: 'pre-wrap' }}>
                <strong>How Scoring Works:</strong><br />
                This score (out of 10) breaks down your trade into 6 areas:<br />
                - <strong>Entry Timing</strong> (2 points): 2 if you entered within 75% of the best price, 1 if you picked the right direction but not the best spot, 0 if you missed it.<br />
                - <strong>Exit Timing</strong> (2 points): 2 if you exited near the best profit point, 1 if you exited early/late but reasonably, 0 if you missed the peak.<br />
                - <strong>Risk-Reward</strong> (2 points): 2 if you got 90% or more of the max possible profit, 1 if you got 70% or more, 0 if less.<br />
                - <strong>Break-Even</strong> (2 points): 2 if no break-even was needed or worked, 1 if you missed a profit chance, 0 if not applicable.<br />
                - <strong>Volatility</strong> (1 point): 1 if your stop-loss fits the market’s movement (ATR), 0 if too tight.<br />
                - <strong>Duration</strong> (1 point): 1 if you held until a key profit point, 0 if you missed it.<br />
                <strong>What ‘Interval Timing’ Means:</strong><br />
                We look at price movements after your entry to find the best opportunities. The time we check depends on the chart’s detail level (called ‘interval’):<br />
                - 1-minute chart: Looks at 30 minutes after entry.<br />
                - 5-minute chart: Looks at 3 hours after entry.<br />
                - 15-minute chart: Looks at 6 hours after entry.<br />
                - 1-hour chart: Looks at 24 hours after entry.<br />
                Plus, we analyze 48 hours after your exit to see the full picture. Your trade used a {selectedInterval} chart.
              </MDTypography>
            }
            placement="left"
            arrow
            sx={{
              maxWidth: '450px',
              minWidth: '350px',
              padding: '8px',
              backgroundColor: '#fff', // Optional: Ensure contrast
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)', // Optional: Slight shadow for box effect
            }}
          >
            <Icon sx={{ ml: 1, cursor: 'pointer' }}>info</Icon>
          </Tooltip>
        </MDBox>
      </MDBox>
                      </MDBox>
                      <Divider sx={{ my: 1 }} />
                      <MDBox pb={2} px={2}>
                        {analysis?.error ? (
                          <MDTypography variant="caption" color="error">
                            Error: {analysis.error}
                          </MDTypography>
                        ) : (
                          <>
                            <MDTypography component="p" variant="caption" color="text" display="flex" fontFamily="inherit">
                              Entry Timing:
                              <MDTypography component="span" variant="caption" fontWeight="bold" color={analysis.entry.score === 2 ? "success" : "error"} fontFamily="inherit" ml={1}>
                                {analysis.entry.score}/2
                              </MDTypography>
                            </MDTypography>
                            <MDTypography variant="caption" color="text" fontFamily="inherit">{analysis.entry.text}</MDTypography>
                            <MDTypography component="p" variant="caption" color="text" display="flex" fontFamily="inherit" mt={1}>
                              Exit Timing:
                              <MDTypography component="span" variant="caption" fontWeight="bold" color={analysis.exit.score === 2 ? "success" : "error"} fontFamily="inherit" ml={1}>
                                {analysis.exit.score}/2
                              </MDTypography>
                            </MDTypography>
                            <MDTypography variant="caption" color="text" fontFamily="inherit">{analysis.exit.text}</MDTypography>
                            <MDTypography component="p" variant="caption" color="text" display="flex" fontFamily="inherit" mt={1}>
                              Risk-Reward:
                              <MDTypography component="span" variant="caption" fontWeight="bold" color={analysis.rr.score === 2 ? "success" : "error"} fontFamily="inherit" ml={1}>
                                {analysis.rr.score}/2
                              </MDTypography>
                            </MDTypography>
                            <MDTypography variant="caption" color="text" fontFamily="inherit">{analysis.rr.text}</MDTypography>
                            <MDTypography component="p" variant="caption" color="text" display="flex" fontFamily="inherit" mt={1}>
                              Break-Even:
                              <MDTypography component="span" variant="caption" fontWeight="bold" color={analysis.be.score === 2 ? "success" : "error"} fontFamily="inherit" ml={1}>
                                {analysis.be.score}/2
                              </MDTypography>
                            </MDTypography>
                            <MDTypography variant="caption" color="text" fontFamily="inherit">{analysis.be.text}</MDTypography>
                            <MDTypography component="p" variant="caption" color="text" display="flex" fontFamily="inherit" mt={1}>
                              Volatility:
                              <MDTypography component="span" variant="caption" fontWeight="bold" color={analysis.volatility.score === 1 ? "success" : "error"} fontFamily="inherit" ml={1}>
                                {analysis.volatility.score}/1
                              </MDTypography>
                            </MDTypography>
                            <MDTypography variant="caption" color="text" fontFamily="inherit">{analysis.volatility.text}</MDTypography>
                            <MDTypography component="p" variant="caption" color="text" display="flex" fontFamily="inherit" mt={1}>
                              Duration:
                              <MDTypography component="span" variant="caption" fontWeight="bold" color={analysis.duration.score === 1 ? "success" : "error"} fontFamily="inherit" ml={1}>
                                {analysis.duration.score}/1
                              </MDTypography>
                            </MDTypography>
                            <MDTypography variant="caption" color="text" fontFamily="inherit">{analysis.duration.text}</MDTypography>
                            <MDTypography component="p" variant="caption" color="text" display="flex" fontFamily="inherit" mt={1}>
                              Optimal Setup:
                              <MDTypography component="span" variant="caption" fontWeight="bold" color="info" fontFamily="inherit" ml={1}>
                                Entry: ${isFinite(analysis.optimalEntry) ? analysis.optimalEntry.toFixed(5) : 'N/A'},
                                SL: ${isFinite(analysis.optimalSL) ? analysis.optimalSL.toFixed(5) : 'N/A'},
                                TP: ${isFinite(analysis.optimalTP) ? analysis.optimalTP.toFixed(5) : 'N/A'}
                              </MDTypography>
                            </MDTypography>
                            <MDTypography variant="caption" color="text" fontFamily="inherit" mt={1}>
                              {analysis.note}
                            </MDTypography>
                          </>
                        )}
                      </MDBox>
                    </MDBox>
                  </Card>
                  <Card
                    ref={tradeDetailsCardRef}
                    sx={{
                      minHeight: { xs: '30vh', md: '20vh' },
                      overflow: 'visible',
                    }}
                  >
                    <MDBox display="flex" flexDirection="column" pt={1} px={2}>
                      <MDBox display="flex" justifyContent="space-between">
                        <MDBox
                          variant="gradient"
                          bgColor="light"
                          color="dark"
                          coloredShadow="light"
                          borderRadius="xl"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          width="4rem"
                          height="4rem"
                          mt={-3}
                          zIndex={1}
                        >
                          <WaterfallChartIcon fontSize="medium" color="inherit" />
                        </MDBox>
                        <MDBox textAlign="right" lineHeight={1.25}>
                          <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
                            Trade Details
                          </MDTypography>
                          <MDTypography variant="h5" fontFamily="inherit">
                            {trade?.shortLong || 'N/A'} Trade
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                      <Divider sx={{ my: 1 }} />
                      <MDBox pb={2} px={2}>
                        <MDTypography component="p" variant="caption" color="text" display="flex" fontFamily="inherit">
                          Symbol:
                          <MDTypography component="span" variant="caption" fontWeight="bold" color="info" fontFamily="inherit" ml={1}>
                            {trade?.symbol || 'N/A'}
                          </MDTypography>
                        </MDTypography>
                        <MDTypography component="p" variant="caption" color="text" display="flex" fontFamily="inherit" mt={1}>
                          Entry Price:
                          <MDTypography component="span" variant="caption" fontWeight="bold" color="text" fontFamily="inherit" ml={1}>
                            ${trade?.entryPrice ? parseFloat(trade.entryPrice).toFixed(5) : (trade?.entry ? parseFloat(trade.entry).toFixed(5) : 'N/A')}
                          </MDTypography>
                        </MDTypography>
                        <MDTypography component="p" variant="caption" color="text" display="flex" fontFamily="inherit" mt={1}>
                          Exit Price:
                          <MDTypography component="span" variant="caption" fontWeight="bold" color="text" fontFamily="inherit" ml={1}>
                            ${trade?.exitPrice ? parseFloat(trade.exitPrice).toFixed(5) : (trade?.exit ? parseFloat(trade.exit).toFixed(5) : 'N/A')}
                          </MDTypography>
                        </MDTypography>
                        <MDTypography component="p" variant="caption" color="text" display="flex" fontFamily="inherit" mt={1}>
                          Stop Loss:
                          <MDTypography component="span" variant="caption" fontWeight="bold" color="error" fontFamily="inherit" ml={1}>
                            ${trade?.sl ? parseFloat(trade.sl).toFixed(5) : 'N/A'}
                          </MDTypography>
                        </MDTypography>
                        <MDTypography component="p" variant="caption" color="text" display="flex" fontFamily="inherit" mt={1}>
                          Take Profit:
                          <MDTypography component="span" variant="caption" fontWeight="bold" color="success" fontFamily="inherit" ml={1}>
                            ${trade?.tp ? parseFloat(trade.tp).toFixed(5) : 'N/A'}
                          </MDTypography>
                        </MDTypography>
                        <MDTypography component="p" variant="caption" color="text" display="flex" fontFamily="inherit" mt={1}>
                          Entry Time:
                          <MDTypography component="span" variant="caption" fontWeight="bold" color="text" fontFamily="inherit" ml={1}>
                            {formatDateUTC(entryDate)}
                          </MDTypography>
                        </MDTypography>
                        <MDTypography component="p" variant="caption" color="text" display="flex" fontFamily="inherit" mt={1}>
                          Exit Time:
                          <MDTypography component="span" variant="caption" fontWeight="bold" color="text" fontFamily="inherit" ml={1}>
                            {formatDateUTC(exitDate)}
                          </MDTypography>
                        </MDTypography>
                        <MDTypography component="p" variant="caption" color="text" display="flex" fontFamily="inherit" mt={1}>
                          Profit/Loss:
                          <MDTypography
                            component="span"
                            variant="caption"
                            fontWeight="bold"
                            color={trade?.returns && parseFloat(trade.returns) >= 0 ? "success" : "error"}
                            fontFamily="inherit"
                            ml={1}
                          >
                            ${trade?.returns ? parseFloat(trade.returns).toFixed(2) : 'N/A'}
                          </MDTypography>
                        </MDTypography>
                        {trade?.comments && (
                          <MDTypography component="p" variant="caption" color="text" fontFamily="inherit" mt={1}>
                            Comments:
                            <MDTypography component="span" variant="caption" color="text" fontFamily="inherit" ml={1}>
                              {trade.comments}
                            </MDTypography>
                          </MDTypography>
                        )}
                      </MDBox>
                    </MDBox>
                  </Card>
                </>
              )}
            </MDBox>
          </MDBox>
        </MDBox>
      </TradeReplayErrorBoundary>
      <Footer />
    </DashboardLayout>
  );
};

export default TradeReplay;